import AssignmentReturnedIcon from '@mui/icons-material/AssignmentReturned';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  ListItemButton,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FileSaver from 'file-saver';
import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { fetchCustomer } from '@sophocles/api/customer';
import {
  bulkSetRadioLocationsDeleted,
  fetchInventoryDashboard,
  fetchRadioLocationExcelReport,
  fetchRadioSiteGroupCounts,
  patchRadioLocation,
} from '@sophocles/api/radioLocations';
import { useUser } from '@sophocles/app';
import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import AssignOrCreateDialog from '@sophocles/components/dialogs/AssignOrCreateDialog';
import ResponsiveDialog from '@sophocles/components/ResponsiveDialog';
import StatCard, { StatCardGroup } from '@sophocles/components/StatCard';
import { EntityGrid } from '@sophocles/data-grids';
import GridStatusCell from '@sophocles/data-grids/components/cells/GridStatusCell';
import { DownloadButton, ToolbarSearchInput } from '@sophocles/data-grids/components/toolbar';
import useGridRefreshRef from '@sophocles/data-grids/datasource/useGridRefreshRef';
import { fetchCustomer as fetchCustomerEntity } from '@sophocles/entities/Customer';
import { fetchSiteGroup } from '@sophocles/entities/SiteGroup';
import { setCustomer } from '@sophocles/features/auth/authActions';
import { setPlant } from '@sophocles/features/sites/slices/selectedPlant/actions';
import { addToast } from '@sophocles/features/toast';
import { parseApiError } from '@sophocles/utils/apiErrors';
import { downloadCsv, rowsToCsv } from '@sophocles/utils/csvExport';
import { userPermCheck } from '@sophocles/utils/userPermissionUtils';
import DeleteInventoryDialog from '@sophocles/views/inventory/dialogs/DeleteInventoryDialog';

const SITE_GROUP_PANEL_WIDTH = 280;

const SORT_FIELD_MAP = {
  iccid: 'radio__iccid',
  timestamp: 'timestamp',
  event: 'event',
  deleted: 'deleted',
};

// Labels are set at render time via translations
const DELETED_OPTION_KEYS = ['active', 'deleted', 'all'];

// ---------------------------------------------------------------------------
// SiteGroupListPanel: sidebar with scrollable site group list
// ---------------------------------------------------------------------------
function SiteGroupListPanel({ customerId, selectedSiteGroupId, onSelect, isBillboard, isMobile, open, onClose }) {
  const { t } = useTranslation();
  const theme = useTheme();

  const [siteGroups, setSiteGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    setLoading(true);
    fetchRadioSiteGroupCounts({ customer: customerId })
      .then(data => {
        setSiteGroups(
          (data || []).map(item => ({
            id: item.site_group.id,
            name: item.site_group.description || item.site_group.name,
            inventory: item.inventory ?? 0,
            on_site: item.on_site ?? 0,
          })),
        );
      })
      .finally(() => setLoading(false));
  }, [customerId]);

  const filteredGroups = useMemo(() => {
    if (!searchText) return siteGroups;
    const lower = searchText.toLowerCase();
    return siteGroups.filter(g => g.name.toLowerCase().includes(lower));
  }, [siteGroups, searchText]);

  const handleSelectGroup = useCallback(
    id => {
      onSelect(id);
      if (isMobile) onClose();
    },
    [onSelect, isMobile, onClose],
  );

  const siteGroupTerm = t('siteGroups.siteGroup');

  const handleExportCsv = useCallback(() => {
    const columns = [
      { field: 'name', headerName: siteGroupTerm },
      { field: 'inventory', headerName: t('inventory.inventory') },
      { field: 'on_site', headerName: t('inventory.onSite') },
      { field: 'total', headerName: t('general.total'), valueGetter: g => g.inventory + g.on_site },
    ];
    const csv = rowsToCsv(columns, siteGroups);
    downloadCsv('site-group-inventory.csv', csv);
  }, [siteGroups, siteGroupTerm, t]);

  const panelContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: isMobile ? 300 : SITE_GROUP_PANEL_WIDTH,
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          px: 1,
          pt: 1,
          pb: 0.5,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 0.5,
          alignItems: 'center',
        }}
      >
        <ToolbarSearchInput placeholder={t('inventory.filterGroups')} value={searchText} onChange={setSearchText} />
        <DownloadButton
          tooltip={t('inventory.exportGroupsCsv')}
          onClick={handleExportCsv}
          disabled={siteGroups.length === 0}
        />
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <ListItemButton
          selected={selectedSiteGroupId == null}
          onClick={() => handleSelectGroup(null)}
          sx={{
            borderLeft:
              selectedSiteGroupId == null ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
            pl: 1.5,
          }}
        >
          <ListItemText
            primary={
              <Typography variant="body2" noWrap sx={{ fontWeight: selectedSiteGroupId == null ? 700 : 400 }}>
                {t('inventory.allGroups')}
              </Typography>
            }
          />
        </ListItemButton>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : filteredGroups.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block' }}>
            {t('inventory.noGroupsFound')}
          </Typography>
        ) : (
          filteredGroups.map(row => {
            const isSelected = row.id === selectedSiteGroupId;
            return (
              <ListItemButton
                key={row.id}
                selected={isSelected}
                onClick={() => handleSelectGroup(row.id)}
                sx={{
                  borderLeft: isSelected ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                  pl: 1.5,
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" noWrap sx={{ fontWeight: isSelected ? 700 : 400 }}>
                      {row.name}
                    </Typography>
                  }
                  secondary={`${t('inventory.invCount', { count: row.inventory })} \u00B7 ${t('inventory.onSiteCount', { count: row.on_site })}`}
                />
              </ListItemButton>
            );
          })
        )}
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer anchor="right" open={open} onClose={onClose}>
        {panelContent}
      </Drawer>
    );
  }

  if (!open) return null;

  return (
    <Box
      sx={{
        width: SITE_GROUP_PANEL_WIDTH,
        flexShrink: 0,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
      }}
    >
      {panelContent}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// InventoryGrid: individual radio location table (adapted from SiteGroupScopeGrid)
// ---------------------------------------------------------------------------
function InventoryGrid({
  siteGroupId,
  customerId,
  isBillboard,
  isStaff,
  canViewDeleted,
  canMutateInventory,
  canAssignToSite,
  hasAllPlantAccess,
  placementFilter,
  onPlacementFilterChange,
  deletedFilter,
  onDeletedFilterChange,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isExporting, setIsExporting] = useState(false);
  const [isPatchingRows, setIsPatchingRows] = useState(false);

  const [focusedRow, setFocusedRow] = useState(null);
  const [isSingleDeleteDialogOpen, setIsSingleDeleteDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  // Rows captured at bulk-action trigger time so the confirmation dialog and
  // mutation callback can reference them after the action bar re-renders.
  const [bulkActionRows, setBulkActionRows] = useState([]);

  // Capture EntityGrid's apiRef and refresh function via onGridReady.
  const inventoryGridApiRef = useRef(null);
  const { onGridReady: captureGridReady, refresh: refreshGrid } = useGridRefreshRef();

  // Placement options — customer mode adds customer/plant inventory sub-filters
  const placementOptions = useMemo(() => {
    const options = [{ value: 'notInstalled', label: t('inventory.notInstalled') }];
    if (!siteGroupId) {
      if (hasAllPlantAccess) {
        options.push({ value: 'customerInventory', label: t('inventory.customerInventory') });
      }
      options.push({
        value: 'plantInventory',
        label: t('inventory.groupInventory'),
      });
    }
    options.push({ value: 'onSite', label: t('inventory.onSite') });
    options.push({ value: 'all', label: t('inventory.all') });
    return options;
  }, [siteGroupId, hasAllPlantAccess, isBillboard, t]);

  // extraParams in JSON:API filter format for the odl-app/radios/ EntityGrid.
  const extraParams = useMemo(() => {
    const p = {};
    if (siteGroupId) {
      p.site_group_id = siteGroupId;
    } else {
      p.customer_id = customerId;
    }
    if (placementFilter !== 'all') p.placement = placementFilter;
    if (canViewDeleted) {
      if (deletedFilter === 'active') p.deleted = 'false';
      else if (deletedFilter === 'deleted') p.deleted = 'true';
    }
    return p;
  }, [siteGroupId, customerId, placementFilter, deletedFilter, canViewDeleted]);

  const placementLabelMap = useMemo(
    () => ({
      onSite: t('inventory.onSite'),
      plantInventory: t('inventory.groupInventory'),
      customerInventory: t('inventory.customerInventory'),
      notInstalled: t('inventory.notInstalled'),
      inventoryLocation: t('inventory.inventory'),
    }),
    [t],
  );

  // fieldOverrides — render overrides on descriptor columns; mirrors RadioGrid patterns.
  const fieldOverrides = useMemo(
    () => ({
      iccid: {
        renderCell: ({ row }) => {
          if (!isStaff || !row.id) return row.iccid || row.mac_address || '';
          return (
            <Button component={Link} to={`/support/radios/${row.id}`} size="small" onClick={e => e.stopPropagation()}>
              {row.iccid || row.mac_address}
            </Button>
          );
        },
      },
      placement_type: {
        renderCell: ({ row }) => {
          const colorMap = {
            onSite: 'ok',
            plantInventory: 'warning',
            customerInventory: 'attention',
            inventoryLocation: 'info',
          };
          const label = placementLabelMap[row.placement_type] ?? row.placement_type;
          return <GridStatusCell color={colorMap[row.placement_type] ?? 'neutral'} label={label} />;
        },
      },
      site_name: {
        minWidth: 240,
        flex: 1.2,
        renderCell: ({ row, value }) => {
          if (!value) return null;
          const loc = row.location;
          let link = null;
          if (row.placement_type === 'onSite' && loc?.customer?.id && loc?.site_group?.id && loc?.site?.id) {
            link = `/c/${loc.customer.id}/plant/${loc.site_group.id}/structure/${loc.site.id}`;
          } else if (row.placement_type === 'plantInventory' && loc?.customer?.id && loc?.site_group?.id) {
            link = `/c/${loc.customer.id}/plant/${loc.site_group.id}/inventory`;
          } else if (row.placement_type === 'customerInventory' && loc?.customer?.id) {
            link = `/c/${loc.customer.id}/inventory`;
          }
          if (!link) {
            return (
              <Typography variant="caption" noWrap>
                {value}
              </Typography>
            );
          }
          return (
            <Button
              component={Link}
              size="small"
              to={link}
              onClick={e => e.stopPropagation()}
              sx={{ justifyContent: 'flex-start' }}
            >
              <Typography variant="caption" noWrap>
                {value}
              </Typography>
            </Button>
          );
        },
      },
      location_timestamp: {
        minWidth: 150,
        renderCell: ({ row }) => {
          const value = row.location_timestamp;
          if (!value) return null;
          return (
            <Tooltip title={moment(value).format('L, LTS')}>
              <Typography variant="caption">{moment(value).fromNow()}</Typography>
            </Tooltip>
          );
        },
      },
    }),
    [isStaff, placementLabelMap],
  );

  // Bespoke columns the descriptor doesn't model: per-row status chip (staff)
  // and action buttons (mutate permission). Appended via options.extraColumns.
  const extraColumns = useMemo(() => {
    const cols = [];
    if (canViewDeleted) {
      cols.push({
        field: 'deleted',
        headerName: t('general.status'),
        minWidth: 120,
        flex: 0.7,
        sortable: false,
        renderCell: params => (
          <GridStatusCell
            color={params.value ? 'bad' : 'ok'}
            label={params.value ? t('inventory.deleted') : t('inventory.active')}
          />
        ),
      });
    }
    if (canMutateInventory) {
      cols.push({
        field: 'inv_actions',
        headerName: '',
        minWidth: 150,
        flex: 0.8,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: params => {
          const isOnSite = params.row.placement_type === 'onSite';
          if (isOnSite && !params.row.deleted) {
            return (
              <Typography variant="caption" color="text.secondary">
                {t('inventory.onSiteLabel')}
              </Typography>
            );
          }
          return (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {canAssignToSite && !isOnSite && !params.row.deleted && (
                <Tooltip title={t('actions.assignOrCreateSite')}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        setFocusedRow(params.row);
                        setIsAssignDialogOpen(true);
                      }}
                    >
                      <AssignmentReturnedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip
                title={
                  params.row.deleted ? t('inventory.restoreInventoryRecord') : t('inventory.deleteInventoryRecord')
                }
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      setFocusedRow(params.row);
                      setIsSingleDeleteDialogOpen(true);
                    }}
                  >
                    {params.row.deleted ? (
                      <RestoreFromTrashIcon fontSize="small" />
                    ) : (
                      <DeleteOutlineIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      });
    }
    return cols;
  }, [canViewDeleted, canMutateInventory, canAssignToSite, t]);

  // Export uses the legacy /inventory/radio-locations/xlsx/ endpoint; build
  // params in its expected format from the same filter state driving extraParams.
  const handleExport = useCallback(() => {
    setIsExporting(true);
    const exportParams = {};
    if (siteGroupId) exportParams.site_group_all = siteGroupId;
    else exportParams.customer_all = customerId;
    if (placementFilter === 'notInstalled') exportParams.site__isnull = true;
    else if (placementFilter === 'customerInventory') {
      exportParams.site__isnull = true;
      exportParams.site_group__isnull = true;
    } else if (placementFilter === 'plantInventory') {
      exportParams.site__isnull = true;
      exportParams.site_group__isnull = false;
    } else if (placementFilter === 'onSite') exportParams.site__isnull = false;
    if (!canViewDeleted) exportParams.deleted = false;
    else if (deletedFilter === 'active') exportParams.deleted = false;
    else if (deletedFilter === 'deleted') exportParams.deleted = true;
    const sortModel = inventoryGridApiRef.current?.current?.getSortModel?.() ?? [];
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      const mappedField = SORT_FIELD_MAP[field] || field;
      exportParams.o = sort === 'desc' ? `-${mappedField}` : mappedField;
    }
    fetchRadioLocationExcelReport(exportParams)
      .then(blob => FileSaver.saveAs(new Blob([blob]), 'inventory-dashboard.xlsx'))
      .finally(() => setIsExporting(false));
  }, [siteGroupId, customerId, placementFilter, deletedFilter, canViewDeleted]);

  const patchRowsDeletedValue = useCallback(
    async targetDeleted => {
      // RadioLocation IDs live at row.location.id on the radios endpoint.
      const ids = bulkActionRows.filter(r => r.deleted !== targetDeleted).map(r => r.location?.id ?? r.id);
      if (ids.length === 0) {
        setBulkAction(null);
        setBulkActionRows([]);
        return;
      }
      setIsPatchingRows(true);
      try {
        const { not_found: notFound = [] } = await bulkSetRadioLocationsDeleted({ deleted: targetDeleted, ids });
        if (notFound.length > 0) {
          dispatch(
            addToast({
              message: t(
                'inventory.bulkDeleteSkipped',
                '{{count}} row(s) were outside your access and left unchanged.',
                { count: notFound.length },
              ),
              level: 'warning',
            }),
          );
        }
      } catch (error) {
        parseApiError(error, { knownFields: [] }).nonFieldMessages.forEach(message =>
          dispatch(addToast({ message, level: 'error' })),
        );
      } finally {
        setIsPatchingRows(false);
        setBulkAction(null);
        setBulkActionRows([]);
        refreshGrid();
      }
    },
    [bulkActionRows, dispatch, t, refreshGrid],
  );

  const handleSingleDeleteToggle = () => {
    if (!focusedRow) return;
    const locationId = focusedRow.location?.id ?? focusedRow.id;
    setIsPatchingRows(true);
    patchRadioLocation(locationId, { deleted: !focusedRow.deleted })
      .then(() => {
        setIsSingleDeleteDialogOpen(false);
        setFocusedRow(null);
        refreshGrid();
      })
      .finally(() => setIsPatchingRows(false));
  };

  const anyLoading = isPatchingRows;

  const filterControls = useMemo(
    () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          flexWrap: 'wrap',
          alignItems: { xs: 'stretch', lg: 'center' },
          gap: 1,
          flex: 1,
          minWidth: 0,
        }}
      >
        <ToggleButtonGroup
          size="small"
          value={placementFilter}
          exclusive
          onChange={(_e, val) => {
            if (val) onPlacementFilterChange(val);
          }}
          sx={{ alignSelf: { xs: 'stretch', lg: 'auto' } }}
        >
          {placementOptions.map(opt => (
            <ToggleButton key={opt.value} value={opt.value} sx={{ flex: { xs: 1, lg: 'none' } }}>
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {canViewDeleted && (
          <ToggleButtonGroup
            size="small"
            value={deletedFilter}
            exclusive
            onChange={(_e, val) => {
              if (val) onDeletedFilterChange(val);
            }}
            sx={{ alignSelf: { xs: 'stretch', lg: 'auto' } }}
          >
            {DELETED_OPTION_KEYS.map(key => (
              <ToggleButton key={key} value={key} sx={{ flex: { xs: 1, lg: 'none' } }}>
                {t(`inventory.${key}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      </Box>
    ),
    [
      placementFilter,
      placementOptions,
      deletedFilter,
      canViewDeleted,
      onPlacementFilterChange,
      onDeletedFilterChange,
      t,
    ],
  );

  const selectionActionSx = { color: 'inherit', textTransform: 'none', fontWeight: 600 };

  const exportButton = useMemo(
    () => (
      <DownloadButton
        tooltip={
          isExporting
            ? t('general.loading', { defaultValue: 'Loading...' })
            : t('general.export', { defaultValue: 'Export' })
        }
        disabled={anyLoading}
        loading={isExporting}
        onClick={handleExport}
      />
    ),
    [anyLoading, isExporting, handleExport, t],
  );

  return (
    <>
      <EntityGrid
        basePath="radios"
        fieldOverrides={fieldOverrides}
        extraParams={extraParams}
        options={{
          showSearch: true,
          infiniteScroll: true,
          defaultSort: [{ field: 'timestamp', sort: 'desc' }],
          toolbarVariant: 'flat',
          toolbarContent: filterControls,
          toolbarActions: exportButton,
          gridProps: {
            density: 'compact',
            loading: isPatchingRows || undefined,
            isRowSelectable: params =>
              canMutateInventory && (params.row.placement_type !== 'onSite' || params.row.deleted),
          },
          selection: canMutateInventory
            ? {
                enabled: true,
                actions: (sel, clear, apiRef) => {
                  const rows = Array.from(sel.ids ?? [])
                    .map(id => apiRef?.current?.getRow(id))
                    .filter(Boolean);
                  const activeCount = rows.filter(r => !r.deleted).length;
                  const deletedCount = rows.filter(r => r.deleted).length;
                  const canAssignSingle = rows.length === 1 && rows[0].placement_type !== 'onSite' && !rows[0].deleted;
                  return (
                    <>
                      {activeCount > 0 && (
                        <Button
                          size="small"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => {
                            setBulkActionRows(rows);
                            setBulkAction('delete');
                          }}
                          disabled={anyLoading || isExporting}
                          sx={selectionActionSx}
                        >
                          {t('actions.delete', 'Delete')}
                        </Button>
                      )}
                      {deletedCount > 0 && (
                        <Button
                          size="small"
                          startIcon={<RestoreFromTrashIcon />}
                          onClick={() => {
                            setBulkActionRows(rows);
                            setBulkAction('restore');
                          }}
                          disabled={anyLoading || isExporting}
                          sx={selectionActionSx}
                        >
                          {t('actions.restore', 'Restore')}
                        </Button>
                      )}
                      {canAssignToSite && canAssignSingle && (
                        <Button
                          size="small"
                          startIcon={<AssignmentReturnedIcon />}
                          onClick={() => {
                            setFocusedRow(rows[0]);
                            setIsAssignDialogOpen(true);
                          }}
                          disabled={anyLoading || isExporting}
                          sx={selectionActionSx}
                        >
                          {t('actions.assign', 'Assign')}
                        </Button>
                      )}
                    </>
                  );
                },
              }
            : undefined,
          extraColumns,
          persistKey: 'inventory-dashboard',
          onGridReady: api => {
            captureGridReady(api);
            inventoryGridApiRef.current = api.apiRef;
          },
        }}
      />

      {focusedRow != null && (
        <DeleteInventoryDialog
          isDeleting={!focusedRow.deleted}
          inventoryItem={focusedRow}
          open={isSingleDeleteDialogOpen}
          onSubmit={handleSingleDeleteToggle}
          onClose={() => {
            setIsSingleDeleteDialogOpen(false);
            setFocusedRow(null);
          }}
        />
      )}

      <AssignOrCreateDialog
        customerId={customerId}
        siteGroupId={siteGroupId}
        radioId={focusedRow?.id}
        open={isAssignDialogOpen}
        onSubmit={() => {
          setIsAssignDialogOpen(false);
          setFocusedRow(null);
          refreshGrid();
        }}
        onClose={() => {
          setIsAssignDialogOpen(false);
          setFocusedRow(null);
        }}
      />

      <ResponsiveDialog
        open={bulkAction != null}
        onClose={() => setBulkAction(null)}
        title={
          bulkAction === 'delete' ? t('inventory.deleteSelectedInventory') : t('inventory.restoreSelectedInventory')
        }
        submitText={bulkAction === 'delete' ? t('actions.delete') : t('actions.restore')}
        onSubmit={() => patchRowsDeletedValue(bulkAction === 'delete')}
      >
        {(() => {
          const actionRows = bulkActionRows.filter(r => (bulkAction === 'delete' ? !r.deleted : r.deleted));
          return (
            <>
              <Typography variant="body2">
                {bulkAction === 'delete'
                  ? t('inventory.deleteSelectedConfirm', { count: actionRows.length })
                  : t('inventory.restoreSelectedConfirm', { count: actionRows.length })}
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                {actionRows.map(r => (
                  <li key={r.id}>
                    <Typography variant="body2" component="span">
                      {r.iccid}
                    </Typography>
                  </li>
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {t('inventory.onlyEditableSelectable')}
              </Typography>
            </>
          );
        })()}
      </ResponsiveDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main dashboard shell: unified master-detail layout
// ---------------------------------------------------------------------------
function InventoryDashboard({ match: { params }, router: { navigate } }) {
  const { customerId, siteGroupId: initialSiteGroupId } = params;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { user, isStaff, permissions } = useUser();
  const canViewDeleted = isStaff;
  const canMutateInventory = userPermCheck(user, 'odl_inventory.change_radiolocation');
  const canAssignToSite = isStaff || permissions.isReseller || permissions.isAtLeastSiteGroupAdmin;
  const hasAllPlantAccess = isStaff || user.get('all_sitegroups_user', false) || permissions.isCustomerAdmin;

  const [selectedSiteGroupId, setSelectedSiteGroupId] = useState(
    initialSiteGroupId ? Number(initialSiteGroupId) : null,
  );
  // The group list is a drawer on mobile and a pinned side panel above it;
  // one control opens the first and hides the second.
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Arriving on one group's own inventory, that group is the subject and the
  // list of the others is a detour; arriving on the customer's inventory, the
  // list is how a group gets chosen at all.
  const [panelHidden, setPanelHidden] = useState(Boolean(initialSiteGroupId));

  // Set current customer/siteGroup in Redux so LeftNav highlights correctly.
  // LeftNav's Customer/Plant sections read the selected-customer *entity*
  // (getSelectedCustomerSelector), not just this id pointer, so the entity
  // fetch is required here too — this view mounts standalone, without a
  // CustomerLayout/SiteGroupLayout ancestor to have already loaded it.
  useEffect(() => {
    dispatch(setCustomer(customerId));
    dispatch(fetchCustomerEntity(customerId));
  }, [customerId, dispatch]);

  useEffect(() => {
    dispatch(setPlant(selectedSiteGroupId || ''));
    if (selectedSiteGroupId) {
      dispatch(fetchSiteGroup(selectedSiteGroupId));
    }
  }, [selectedSiteGroupId, dispatch]);

  const handleSiteGroupSelect = useCallback(
    id => {
      setSelectedSiteGroupId(id);
      const path = id ? `/c/${customerId}/plant/${id}/inventory` : `/c/${customerId}/inventory`;
      navigate(path, { replace: true });
    },
    [customerId, navigate],
  );

  // Customer name + billboard flag for title and terminology
  const [customerName, setCustomerName] = useState('');
  const [isBillboard, setIsBillboard] = useState(true);
  const [isContextLoading, setIsContextLoading] = useState(false);

  useEffect(() => {
    let ignore = false;
    setIsContextLoading(true);
    fetchCustomer(customerId)
      .then(customer => {
        if (!ignore) {
          setCustomerName(_.get(customer, 'name', `Customer ${customerId}`));
          setIsBillboard(_.get(customer, 'is_billboard', true));
        }
      })
      .finally(() => {
        if (!ignore) setIsContextLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [customerId]);

  // Dashboard summary
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryData, setSummaryData] = useState({
    total: 0,
    active: 0,
    deleted: 0,
    on_site: 0,
    in_inventory: 0,
    customer_inventory: 0,
    site_group_inventory: 0,
  });

  const dashboardParams = useMemo(
    () =>
      selectedSiteGroupId
        ? { scope: 'site_group', site_group_id: selectedSiteGroupId }
        : { scope: 'customer', customer_id: customerId },
    [customerId, selectedSiteGroupId],
  );

  useEffect(() => {
    setSummaryLoading(true);
    fetchInventoryDashboard(dashboardParams)
      .then(data => setSummaryData(data.summary || {}))
      .finally(() => setSummaryLoading(false));
  }, [dashboardParams]);

  // Filters are owned here so cards can drive them
  const [placementFilter, setPlacementFilter] = useState('notInstalled');
  const [deletedFilter, setDeletedFilter] = useState('active');

  // Reset placement filter when switching to a site group (customer/plant inventory only valid at customer level)
  useEffect(() => {
    if (selectedSiteGroupId && (placementFilter === 'customerInventory' || placementFilter === 'plantInventory')) {
      setPlacementFilter('notInstalled');
    }
  }, [selectedSiteGroupId, placementFilter]);

  const summaryCards = useMemo(() => {
    const cards = [
      {
        label: t('general.total'),
        value: summaryData.active ?? 0,
        placement: 'all',
        deleted: 'active',
        color: '#4f46e5',
      },
      {
        label: t('inventory.onSite'),
        value: summaryData.on_site ?? 0,
        placement: 'onSite',
        deleted: 'active',
        color: 'success.main',
      },
      {
        label: t('inventory.notInstalled'),
        value: summaryData.in_inventory ?? 0,
        placement: 'notInstalled',
        deleted: 'active',
        color: 'info.main',
      },
    ];
    if (!selectedSiteGroupId) {
      if (hasAllPlantAccess) {
        cards.push({
          label: t('inventory.customerInventory'),
          value: summaryData.customer_inventory ?? 0,
          placement: 'customerInventory',
          deleted: 'active',
          color: '#7c3aed',
        });
      }
      cards.push({
        label: t('inventory.groupInventory'),
        value: summaryData.site_group_inventory ?? 0,
        placement: 'plantInventory',
        deleted: 'active',
        color: 'warning.main',
      });
    }
    if (canViewDeleted) {
      cards.push({
        label: t('inventory.deleted'),
        value: summaryData.deleted ?? 0,
        placement: 'all',
        deleted: 'deleted',
        color: 'error.main',
      });
    }
    return cards;
  }, [canViewDeleted, hasAllPlantAccess, selectedSiteGroupId, summaryData, isBillboard, t]);

  const isCardActive = useCallback(
    card => {
      if (!card.placement) return false;
      return placementFilter === card.placement && deletedFilter === card.deleted;
    },
    [placementFilter, deletedFilter],
  );

  const handleCardClick = useCallback(
    card => {
      if (!card.placement) return;
      if (isCardActive(card)) {
        // Toggle off → reset to defaults
        setPlacementFilter('notInstalled');
        setDeletedFilter('active');
      } else {
        setPlacementFilter(card.placement);
        setDeletedFilter(card.deleted);
      }
    },
    [isCardActive],
  );

  // For the Grid size prop: when site group selected we have 3 or 4 cards, when customer level we have 5 or 6
  const cardLgSize = selectedSiteGroupId ? (canViewDeleted ? 3 : 4) : canViewDeleted ? 2 : 2.4;

  return (
    <AppNav loading={isContextLoading} page={selectedSiteGroupId ? 'plant_inventory' : 'customer_inventory'} flex>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflow: 'hidden',
          px: { xs: 1, md: 2 },
          pb: 2,
          gap: 1.5,
        }}
      >
        {/* The breadcrumbs already name the selected group, so the control
            beside them only has to say "show me the list". */}
        <TitleBar
          showBackButton
          title={t('inventory.inventory')}
          actions={
            <Tooltip title={t('inventory.groupList')}>
              <IconButton
                size="small"
                aria-label={t('inventory.groupList')}
                aria-expanded={isMobile ? drawerOpen : !panelHidden}
                onClick={() => (isMobile ? setDrawerOpen(true) : setPanelHidden(hidden => !hidden))}
              >
                <ViewSidebarOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
          sx={{ pt: 0.5, pb: 0 }}
        />

        {/* Summary cards */}
        <StatCardGroup
          columns={{ xs: 2, sm: 3, lg: Math.min(summaryCards.length, 6) }}
          gap={1.5}
          sx={{ flexShrink: 0 }}
        >
          {summaryCards.map(card => {
            const clickable = !!card.placement;
            return (
              <StatCard
                key={card.label}
                title={card.label}
                value={card.value}
                color={card.color}
                loading={summaryLoading}
                active={isCardActive(card)}
                onClick={clickable ? () => handleCardClick(card) : undefined}
              />
            );
          })}
        </StatCardGroup>

        {/* Master-detail layout */}
        <Box sx={{ display: 'flex', flex: 1, gap: 2, minHeight: 0 }}>
          <SiteGroupListPanel
            customerId={customerId}
            selectedSiteGroupId={selectedSiteGroupId}
            onSelect={handleSiteGroupSelect}
            isBillboard={isBillboard}
            isMobile={isMobile}
            open={isMobile ? drawerOpen : !panelHidden}
            onClose={() => setDrawerOpen(false)}
          />

          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <InventoryGrid
              siteGroupId={selectedSiteGroupId}
              customerId={customerId}
              isBillboard={isBillboard}
              isStaff={isStaff}
              canViewDeleted={canViewDeleted}
              canMutateInventory={canMutateInventory}
              canAssignToSite={canAssignToSite}
              hasAllPlantAccess={hasAllPlantAccess}
              placementFilter={placementFilter}
              onPlacementFilterChange={setPlacementFilter}
              deletedFilter={deletedFilter}
              onDeletedFilterChange={setDeletedFilter}
            />
          </Box>
        </Box>
      </Box>
    </AppNav>
  );
}

export default InventoryDashboard;
